import React, { useCallback, useEffect, useState } from 'react';
import { Clock, ExternalLink, Trash2 } from 'lucide-react';
import { getOfferBadge } from '../utils/offerUtils';
import { SOURCE_LOGO } from './MesAlertes';
import { auth } from '../firebase';
import { apiHeaders } from '../utils/api';

interface FavoriRow {
  id: number;
  newsletter_slug: string;
  url: string;
  title: string;
  company: string;
  location: string;
  source: string;
  date: string;
  remuneration: string;
  type_contrat_detail: string;
  created_at: string;
}


function formatRelative(createdAt: string): string {
  if (!createdAt) return '';
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / (86400 * 1000));
  if (days <= 0) return "Aujourd'hui";
  if (days === 1) return 'Hier';
  return `Il y a ${days}j`;
}

const Favoris: React.FC = () => {
  const [rows, setRows] = useState<FavoriRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<number | null>(null);

  const load = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const res = await fetch('https://aglomera.com/api/favoris/', {
        headers: await apiHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setRows(Array.isArray(data) ? data : []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: number) => {
    const user = auth.currentUser;
    if (!user) return;
    setDeleting(id);
    try {
      const res = await fetch(`https://aglomera.com/api/favoris/${id}/`, {
        method: 'DELETE',
        headers: await apiHeaders(),
      });
      if (res.ok) {
        setRows(prev => prev.filter(r => r.id !== id));
      }
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 rounded-full border-2 animate-spin"
          style={{ borderColor: '#2a52a8', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="p-8 w-full">
      <div className="mb-8">
        <h1 id="favoris-page-anchor" className="text-2xl font-bold text-gray-900">Favoris</h1>
        <p className="text-sm text-gray-500 mt-1">Offres que vous avez marquées avec une étoile</p>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center text-gray-500 text-sm">
          Aucun favori pour l'instant. Ouvrez une newsletter et cliquez sur l'étoile à côté d'une offre pour l'ajouter ici.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map(row => {
            const isDeleting = deleting === row.id;

            return (
              <div
                key={row.id}
                className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4 hover:border-gray-200 transition-all group"
              >
                {/* Badge portail : logo ou initiales */}
                {(() => {
                  const badge = getOfferBadge({ source: row.source, location: row.location }, SOURCE_LOGO);
                  return (
                    <div className="w-10 h-10 rounded-xl border border-gray-100 bg-gray-50 flex items-center justify-center flex-shrink-0 p-1.5">
                      {badge.type === 'logo' ? (
                        <img src={badge.logo} alt={badge.label} title={badge.label}
                          className="w-full h-full object-contain"
                          onError={e => {
                            const t = e.currentTarget as HTMLImageElement;
                            t.style.display = 'none';
                            const p = t.parentElement;
                            const init = badge.label.replace(/[_-]/g,' ').split(/\s+/).filter(Boolean).slice(0,3).map((w:string)=>w[0]).join('').toUpperCase()||'??';
                            if (p) { const s = document.createElement('span'); s.style.cssText='font-size:9px;font-weight:700;color:#6b7280'; s.textContent=init; p.appendChild(s); } // L5
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', letterSpacing: '0.03em' }}>
                          {badge.text}
                        </span>
                      )}
                    </div>
                  );
                })()}

                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{row.title}</h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {row.company || '—'}
                    {row.location ? ` · ${row.location}` : ''}
                  </p>
                  {row.newsletter_slug && (
                    <p className="text-xs mt-1" style={{ color: '#2a52a8' }}>
                      Newsletter : {row.newsletter_slug}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <Clock size={12} />
                    {formatRelative(row.created_at)}
                  </div>
                  {row.url && (
                    <a href={row.url} target="_blank" rel="noopener noreferrer"
                      className="p-2 rounded-xl hover:bg-gray-50 text-gray-400 hover:text-gray-700 transition-all">
                      <ExternalLink size={16} />
                    </a>
                  )}
                  {/* Bouton supprimer */}
                  <button
                    onClick={() => handleDelete(row.id)}
                    disabled={isDeleting}
                    title="Supprimer ce favori"
                    className="p-2 rounded-xl transition-all text-gray-300 hover:text-red-500 hover:bg-red-50 disabled:opacity-40"
                  >
                    {isDeleting ? (
                      <div className="w-4 h-4 rounded-full border-2 animate-spin"
                        style={{ borderColor: '#ef4444', borderTopColor: 'transparent' }} />
                    ) : (
                      <Trash2 size={15} />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};

export default Favoris;
